import { useState, useEffect, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import type { Building } from 'models';
import useCooperativeId from 'shared/hooks/useCooperativeId';
import { buildingsFields } from 'configs';
import { createTabsConfig } from 'utils';

import { fileLoaderApi, FileLoader, EntityType } from 'features/file-loader';

import PageTitle from 'containers/page-title';
import {
  Tabs,
  PageLoader,
  BorderedBox,
  Flex,
  TitledBox,
  FieldsList,
  NoData,
} from 'components';
import { useStyles } from './style';

interface Props {
  buildings: Building[];
  loading: boolean;
}

const isDocumentsQueryCached = true;

const BuildingsDetails: React.FC<Props> = ({ buildings, loading }) => {
  const classes = useStyles();
  const cooperativeId = useCooperativeId();

  const [currentTab, setCurrentTab] = useState<string | null>(null);

  const tabsConfig = useMemo(() => createTabsConfig(buildings), [buildings]);
  const selectedBuilding = useMemo(() => {
    return buildings.find((building) => building.Id === currentTab);
  }, [currentTab, buildings]);

  fileLoaderApi.useGetAttachmentsQuery(
    cooperativeId && selectedBuilding
      ? {
          cooperativeId,
          entityId: selectedBuilding.Id,
          entityType: EntityType.Building,
          cached: isDocumentsQueryCached,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (buildings.length) {
      setCurrentTab(buildings[0].Id);
    }
  }, [buildings]);

  if (loading) {
    return <PageLoader />;
  }

  if (!buildings.length || !selectedBuilding || !currentTab) {
    return <NoData />;
  }

  const handleChangeCurrentTab = (id: string) => {
    setCurrentTab(id);
  };

  return (
    <div>
      <PageTitle />
      <Tabs
        tabs={tabsConfig}
        activeTab={currentTab}
        onSelect={handleChangeCurrentTab}
        cls={{
          ul: classes.tabs,
        }}
      />
      <BorderedBox>
        <Flex>
          {Object.keys(buildingsFields).map((key) => {
            const fieldItem = buildingsFields[key];

            return (
              <TitledBox key={key} title={fieldItem.title}>
                <FieldsList
                  data={selectedBuilding}
                  list={fieldItem.fieldsList}
                />
              </TitledBox>
            );
          })}
          <TitledBox cls={{ box: classes.documentsBox }} title={'Dokumentit'}>
            <FileLoader
              cached={isDocumentsQueryCached}
              entityId={selectedBuilding.Id}
              entityType={EntityType.Building}
            />
          </TitledBox>
        </Flex>
      </BorderedBox>
    </div>
  );
};

export default BuildingsDetails;
